import { useState, useEffect, useCallback } from 'react';
import { backofficeUsers, type BackofficeUser } from '@/mocks/backoffice/users';
import {
  getUsuariosByEmpresaId,
  adicionarUsuarioEmpresa,
  removerUsuarioEmpresa,
  getRoleNaEmpresa,
  type EmpresaUsuario,
} from '@/mocks/backoffice/empresaUsuarios';
import type { BackofficeRole } from '@/contexts/BackofficeAuthContext';

interface EmpresaUsuariosTabProps {
  empresaId: string;
  empresaNome: string;
}

const ROLE_LABELS: Record<BackofficeRole, string> = {
  admin: 'Admin',
  suporte: 'Suporte',
  financeiro: 'Financeiro',
  operador: 'Operador',
};

const ROLE_COLORS: Record<BackofficeRole, string> = {
  admin: 'bg-purple-100 text-purple-700',
  suporte: 'bg-blue-100 text-blue-700',
  financeiro: 'bg-amber-100 text-amber-700',
  operador: 'bg-gray-100 text-gray-700',
};

export default function EmpresaUsuariosTab({ empresaId, empresaNome }: EmpresaUsuariosTabProps) {
  const [usuariosComAcesso, setUsuariosComAcesso] = useState<EmpresaUsuario[]>([]);
  const [usuariosSemAcesso, setUsuariosSemAcesso] = useState<BackofficeUser[]>([]);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [editandoUserId, setEditandoUserId] = useState<string | null>(null);

  const recarregar = useCallback(() => {
    const comAcesso = getUsuariosByEmpresaId(empresaId);
    setUsuariosComAcesso(comAcesso);

    const idsComAcesso = comAcesso.map(u => u.userId);
    const semAcesso = backofficeUsers.filter(u => !idsComAcesso.includes(u.id));
    setUsuariosSemAcesso(semAcesso);
  }, [empresaId]);

  useEffect(() => {
    recarregar();
  }, [recarregar]);

  const mostrarToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleAdicionar = (userId: string, role: BackofficeRole) => {
    adicionarUsuarioEmpresa(userId, empresaId, role);
    recarregar();
    const user = backofficeUsers.find(u => u.id === userId);
    mostrarToast(`${user?.nome || 'Usuário'} adicionado como ${ROLE_LABELS[role]}`, 'success');
    setEditandoUserId(null);
  };

  const handleRemover = (userId: string) => {
    removerUsuarioEmpresa(userId, empresaId);
    recarregar();
    const user = backofficeUsers.find(u => u.id === userId);
    mostrarToast(`${user?.nome || 'Usuário'} removido do acesso`, 'success');
  };

  const handleAlterarRole = (userId: string, novoRole: BackofficeRole) => {
    adicionarUsuarioEmpresa(userId, empresaId, novoRole);
    recarregar();
    const user = backofficeUsers.find(u => u.id === userId);
    mostrarToast(`Papel de ${user?.nome || 'Usuário'} alterado para ${ROLE_LABELS[novoRole]}`, 'success');
    setEditandoUserId(null);
  };

  const getUserById = (id: string) => backofficeUsers.find(u => u.id === id);

  return (
    <div className="space-y-6">
      {/* Header com stats */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">
            Usuários com Acesso &mdash; {empresaNome}
          </h3>
          <p className="text-xs text-gray-400 mt-1">
            Gerencie quais usuários do backoffice podem acessar e operar esta empresa
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-800">{usuariosComAcesso.length}</p>
            <p className="text-xs text-gray-400">usuários com acesso</p>
          </div>
          <div className="w-px h-10 bg-gray-200"></div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-400">{usuariosSemAcesso.length}</p>
            <p className="text-xs text-gray-400">sem acesso</p>
          </div>
        </div>
      </div>

      {/* Tabela de usuários com acesso */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
          <p className="text-xs font-bold text-gray-500 uppercase">Com acesso</p>
        </div>
        {usuariosComAcesso.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-user-unfollow-line text-gray-300 text-xl"></i>
              </div>
            </div>
            <p className="text-sm text-gray-400">Nenhum usuário com acesso a esta empresa</p>
            <p className="text-xs text-gray-300 mt-1">Adicione usuários na seção abaixo</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {usuariosComAcesso.map((eu) => {
              const user = getUserById(eu.userId);
              if (!user) return null;
              const editando = editandoUserId === eu.userId;
              return (
                <div key={eu.userId} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50/50 transition-all duration-150">
                  <div className="flex items-center space-x-3">
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.nome}
                        className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-user-line text-gray-400"></i>
                        </div>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-bold text-gray-800">{user.nome}</p>
                      <p className="text-xs text-gray-400">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {editando ? (
                      <div className="flex items-center space-x-2 bg-gray-50 rounded-lg px-2 py-1.5">
                        {(['admin', 'suporte', 'financeiro', 'operador'] as BackofficeRole[]).map((r) => (
                          <button
                            key={r}
                            onClick={() => handleAlterarRole(eu.userId, r)}
                            className={`px-2.5 py-1 rounded-md text-xs font-bold transition-all duration-150 cursor-pointer whitespace-nowrap ${
                              eu.role === r
                                ? 'bg-emerald-500 text-white'
                                : 'text-gray-500 hover:bg-gray-200'
                            }`}
                          >
                            {ROLE_LABELS[r]}
                          </button>
                        ))}
                        <button
                          onClick={() => setEditandoUserId(null)}
                          className="ml-1 p-1 rounded hover:bg-gray-200 text-gray-400 cursor-pointer"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-close-line text-sm"></i>
                          </div>
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold ${ROLE_COLORS[eu.role]}`}>
                          {ROLE_LABELS[eu.role]}
                        </span>
                        <button
                          onClick={() => setEditandoUserId(eu.userId)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all duration-150 cursor-pointer"
                          title="Alterar papel"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-edit-line text-sm"></i>
                          </div>
                        </button>
                        <button
                          onClick={() => handleRemover(eu.userId)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all duration-150 cursor-pointer"
                          title="Remover acesso"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </div>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Adicionar usuários */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
          <p className="text-xs font-bold text-gray-500 uppercase">Adicionar usuários</p>
        </div>
        {usuariosSemAcesso.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-check-double-line text-emerald-400 text-xl"></i>
              </div>
            </div>
            <p className="text-sm text-gray-400">Todos os usuários já têm acesso a esta empresa</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {usuariosSemAcesso.map((user) => (
              <div key={user.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50/50 transition-all duration-150">
                <div className="flex items-center space-x-3">
                  {user.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.nome}
                      className="w-9 h-9 rounded-full object-cover flex-shrink-0 opacity-50"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-user-line text-gray-300"></i>
                      </div>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-bold text-gray-400">{user.nome}</p>
                    <p className="text-xs text-gray-300">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-1.5">
                  {(['operador', 'financeiro', 'suporte', 'admin'] as BackofficeRole[]).map((r) => (
                    <button
                      key={r}
                      onClick={() => handleAdicionar(user.id, r)}
                      className={`px-2.5 py-1 rounded-md text-xs font-bold transition-all duration-150 cursor-pointer whitespace-nowrap border ${
                        r === 'admin'
                          ? 'border-purple-200 text-purple-600 hover:bg-purple-50'
                          : r === 'suporte'
                          ? 'border-blue-200 text-blue-600 hover:bg-blue-50'
                          : r === 'financeiro'
                          ? 'border-amber-200 text-amber-600 hover:bg-amber-50'
                          : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      + {ROLE_LABELS[r]}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-xl shadow-lg text-sm font-bold flex items-center space-x-2 z-50 ${
          toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-500 text-white'
        }`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-${toast.type === 'success' ? 'check-line' : 'error-warning-line'} text-sm`}></i>
          </div>
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}